import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { TicketFormData } from '../../types';
import { useToast } from '../../components/shared';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const NewTicket: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // const { user } = useAuth(); // Kullanılmıyor
  const { createTicket, releases } = useData();
  const { showToast } = useToast();
  
  const [formData, setFormData] = useState<TicketFormData>({
    subject: '',
    description: '',
    category: 'general',
    priority: 'normal',
    release_id: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<TicketFormData>>({});

  // Kullanıcının yayınlanmış release'lerini filtrele
  const userReleases = releases.filter(release => 
    release.status === 'published' || release.status === 'approved'
  );

  const validateForm = () => {
    const newErrors: Partial<TicketFormData> = {};
    
    if (!formData.subject.trim()) {
      newErrors.subject = t('support.validation.subjectRequired');
    } else if (formData.subject.length < 10) {
      newErrors.subject = t('support.validation.subjectTooShort');
    }
    
    if (!formData.description.trim()) {
      newErrors.description = t('support.validation.descriptionRequired');
    } else if (formData.description.length < 10) {
      newErrors.description = t('support.validation.descriptionTooShort');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await createTicket(formData);
      showToast('success', t('support.ticketCreated'));
      navigate('/support');
    } catch (error) {
      showToast('error', t('support.ticketCreationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof TicketFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <button
            onClick={() => navigate('/support')}
            className="p-2 rounded-lg bg-gray-800/50 border border-gray-600 text-gray-300 hover:bg-gray-700/50 hover:text-white transition-all duration-200 backdrop-blur-sm"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-white">
            {t('support.newTicket')}
          </h1>
        </div>
        <p className="text-gray-300">
          {t('support.newTicketDescription')}
        </p>
      </div>

      {/* Form */}
      <div className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8 shadow-lg">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.subject')} <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              className={`w-full px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent backdrop-blur-sm transition-all duration-200 ${
                errors.subject 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-600 focus:ring-purple-500'
              }`}
              placeholder={t('support.subjectPlaceholder')}
            />
            {errors.subject && (
              <p className="mt-1 text-sm text-red-400">{errors.subject}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.category')}
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
            >
              <option value="general">{t('support.categories.general')}</option>
              <option value="technical">{t('support.categories.technical')}</option>
              <option value="billing">{t('support.categories.billing')}</option>
              <option value="distribution">{t('support.categories.distribution')}</option>
              <option value="account">{t('support.categories.account')}</option>
            </select>
          </div>

          {/* Release Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.relatedMusic')}
            </label>
            <select
              value={formData.release_id}
              onChange={(e) => handleInputChange('release_id', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
            >
              <option value="">{t('support.selectMusic')}</option>
              {userReleases.map(release => (
                <option key={release.id} value={release.id}>
                  {release.title} ({release.type})
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-400">
              {t('support.relatedMusicHelp')}
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.priority')}
            </label>
            <select
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent backdrop-blur-sm transition-all duration-200"
            >
              <option value="low">{t('support.priorities.low')}</option>
              <option value="normal">{t('support.priorities.normal')}</option>
              <option value="high">{t('support.priorities.high')}</option>
              <option value="urgent">{t('support.priorities.urgent')}</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('support.description')} <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={6}
              className={`w-full px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent backdrop-blur-sm transition-all duration-200 resize-none ${
                errors.description 
                  ? 'border-red-500 focus:ring-red-500' 
                  : 'border-gray-600 focus:ring-purple-500'
              }`}
              placeholder={t('support.descriptionPlaceholder')}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-400">{errors.description}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex items-center space-x-4 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 disabled:from-gray-600 disabled:to-gray-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('common.loading') : t('support.createTicket')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/support')}
              className="px-8 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700/50 hover:text-white transition-all duration-200 backdrop-blur-sm"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTicket; 